/**
 * Web sign-in gate (design/exports/account/web-1, web-2a, web-2b): shown over the menus (never mid-match) while this
 * browser has no linked account. Apple's and Google's official buttons (Apple JS rendered button + GIS renderButton)
 * sign in (the guest underneath is linked, so its coins come along); PLAY OFFLINE lifts the gate for the session.
 * States: idle → SIGNING IN… (waiting for the provider window; CANCEL) → FUMBLE! (TRY AGAIN / PLAY OFFLINE) on error.
 * Pad / keyboard: ▲ ▼ between the items, A confirms.
 */
import { PublicInfo } from '../PublicInfo';
import { useEffect, useRef, useState } from 'preact/hooks';
import { ui } from '@hf/client-core/ui-model/store';
import '../screens/settings.css';
import { renderGoogleButton } from '../../net/google-web-signin';
import { mountAppleButton, signInWithAppleWeb, type AppleWebOutcome } from '../../net/apple-web-signin';
import { account } from '../../net/account';
import { Head } from '../components';
import { LAYER, useFocusGrid } from '../nav';
import { accountDialog } from './AccountDialogs';
import {
  isWebSignedIn,
  linkAppleWeb,
  linkGoogleWeb,
  refreshWebAccount,
  webAccount,
  webAccountChecked,
  webPlayingOffline,
} from '../wallet';

/** Both official buttons render at this size (GIS "large" is 40px tall; Apple's is set to match). */
const BUTTON_W = 260;
const BUTTON_H = 40;

const NOTE = {
  offline: ['No connection. Check your connection and try again.', 'network_offline'],
  error: ["Sign-in didn't go through. Check your connection and try again.", 'link_failed'],
  merge_blocked: [
    'That account already has purchases on another profile. Contact support to merge.',
    'merge_blocked',
  ],
  unavailable: ['Sign-in is unavailable here.', 'unavailable'],
} as const;

type Provider = 'Apple' | 'Google';
type GateState =
  { k: 'idle' } | { k: 'loading'; who: Provider } | { k: 'error'; who: Provider; msg: string; code: string };

export const webGateVisible = (): boolean => {
  const screen = ui.screen.value;
  void webAccount.value; // subscribe
  return (
    !!account &&
    webAccountChecked.value &&
    !webPlayingOffline.value &&
    accountDialog.value === null &&
    !isWebSignedIn() &&
    screen !== 'boot' &&
    screen !== 'match' &&
    screen !== 'versus'
  );
};

export function SignInGate() {
  useEffect(() => {
    void refreshWebAccount().finally(() => (webAccountChecked.value = true));
  }, []);
  if (!webGateVisible()) return null;
  return <GateCard />;
}

function GateCard() {
  const [st, setSt] = useState<GateState>({ k: 'idle' });
  const playOffline = (): void => {
    webPlayingOffline.value = true;
  };
  // A cancelled wait ignores the provider's late answer.
  const attempt = useRef(0);
  const link = async (who: Provider, run: () => ReturnType<typeof linkGoogleWeb>): Promise<void> => {
    const n = ++attempt.current;
    setSt({ k: 'loading', who });
    const r = await run();
    if (n !== attempt.current) return;
    if (r === 'ok') return; // signed in: the gate closes
    setSt({ k: 'error', who, msg: NOTE[r][0], code: NOTE[r][1] });
  };
  const onAppleResult = (s: AppleWebOutcome, n: number): void => {
    if (n !== attempt.current) return;
    if (!s.ok) {
      if (s.reason === 'cancelled') setSt({ k: 'idle' });
      else setSt({ k: 'error', who: 'Apple', msg: NOTE.error[0], code: s.detail ?? 'apple_failed' });
      return;
    }
    void link('Apple', () => linkAppleWeb(s));
  };
  const appleViaPad = async (): Promise<void> => {
    const n = ++attempt.current;
    setSt({ k: 'loading', who: 'Apple' });
    onAppleResult(await signInWithAppleWeb(), n);
  };
  const cancel = (): void => {
    attempt.current++;
    setSt({ k: 'idle' });
  };
  if (st.k === 'loading') return <GateLoading who={st.who} onCancel={cancel} />;
  if (st.k === 'error')
    return (
      <GateError msg={st.msg} code={`${st.code} · ${st.who}`} onRetry={cancel} onOffline={playOffline} />
    );
  return (
    <GateIdle
      onApplePad={() => void appleViaPad()}
      onAppleClick={(s) => {
        const n = ++attempt.current;
        onAppleResult(s, n);
      }}
      onAppleStart={() => setSt({ k: 'loading', who: 'Apple' })}
      onGoogle={(tok) => void link('Google', () => linkGoogleWeb(tok))}
      onGoogleFail={() =>
        setSt({
          k: 'error',
          who: 'Google',
          msg: 'Could not load Google sign-in. Check your connection or ad blocker.',
          code: 'gsi_load_failed',
        })
      }
      onOffline={playOffline}
    />
  );
}

function GateIdle(p: {
  onApplePad(): void;
  onAppleClick(s: AppleWebOutcome): void;
  onAppleStart(): void;
  onGoogle(tok: string): void;
  onGoogleFail(): void;
  onOffline(): void;
}) {
  const host = useRef<HTMLDivElement | null>(null);
  const cb = useRef(p);
  cb.current = p;
  // 0 Apple · 1 Google · 2 Play offline. Google's button is a cross-origin iframe: a pad can focus it, but only a
  // click / tap opens its popup.
  const grid = useFocusGrid({
    count: 3,
    initial: 2,
    active: true,
    priority: LAYER.MODAL,
    onConfirm: (i) => {
      if (i === 0) p.onApplePad();
      else if (i === 2) p.onOffline();
    },
  });
  useEffect(() => mountAppleButton((s) => cb.current.onAppleClick(s)), []);
  useEffect(() => {
    const el = host.current;
    if (!el) return;
    let live = true;
    renderGoogleButton(el, (tok) => live && cb.current.onGoogle(tok), BUTTON_W, 'outline', 'center').catch(
      () => live && cb.current.onGoogleFail(),
    );
    return () => {
      live = false;
    };
  }, []);
  return (
    <div
      class="hf-gate"
      role="dialog"
      aria-modal="true"
      aria-label="Sign in to play"
      data-testid="sign-in-gate"
    >
      <div class="hf-gate__brand t-display" aria-hidden="true">
        HAND EGG
      </div>
      <div class="hf-gate__card hf-dlg">
        <div class="hf-gate__avatar">
          <Head character="rookie" expression="determined" />
        </div>
        <h2 class="t-display hf-gate__title">SIGN IN TO PLAY</h2>
        <p class="hf-gate__sub">Save your coins, heads and Elo across web, iPhone and Android.</p>
        <div class="hf-gate__buttons">
          <div
            class="hf-gate__apple interactive"
            data-testid="sign-in-gate-apple"
            {...grid.item(0)}
            onClickCapture={() => p.onAppleStart()}
          >
            {/* Apple JS renders its official black "Sign in with Apple" button into #appleid-signin. */}
            <div
              id="appleid-signin"
              data-mode="center-align"
              data-type="sign-in"
              data-color="black"
              data-border="false"
              data-border-radius="50"
              data-width={String(BUTTON_W)}
              data-height={String(BUTTON_H)}
              style={`width:${BUTTON_W}px;height:${BUTTON_H}px;cursor:pointer`}
            />
          </div>
          <div class="hf-gate__google interactive" data-testid="sign-in-gate-google" {...grid.item(1)}>
            {/* Our own plain-English "Sign in with Google" face (Google branding guidelines: white, official G, Roboto
                500). GIS' real button sits invisibly on top so the click still runs Google's popup and returns the ID
                token, without GIS personalising it ("Continue as …") or localising it. */}
            <span class="hf-gate__gface" aria-hidden="true">
              <svg viewBox="0 0 48 48" width="18" height="18">
                <path
                  fill="#EA4335"
                  d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                />
                <path
                  fill="#4285F4"
                  d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                />
                <path
                  fill="#FBBC05"
                  d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                />
                <path
                  fill="#34A853"
                  d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                />
              </svg>
              <span>Sign in with Google</span>
            </span>
            <div ref={host} class="hf-gate__gsi" />
          </div>
        </div>
        <div class="hf-gate__or" aria-hidden="true">
          <span>OR</span>
        </div>
        <button
          type="button"
          class="hf-gate__offline interactive"
          data-testid="sign-in-gate-offline"
          {...grid.item(2)}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              d="M2 8.8a15 15 0 0 1 20 0M5 12.6a10 10 0 0 1 14 0M8.5 16.4a5 5 0 0 1 7 0M12 20h0M3 3l18 18"
            />
          </svg>
          <span class="t-display">PLAY OFFLINE</span>
        </button>
        <p class="hf-gate__fine">
          Offline: bot matches only. Guest coins can't be restored after reinstalling.
        </p>
        <p class="hf-gate__legal">
          By continuing you agree to the{' '}
          <a href="https://handegg-legal.pages.dev/terms" target="_blank" rel="noopener">
            Terms
          </a>{' '}
          and{' '}
          <a href="https://handegg-legal.pages.dev/privacy" target="_blank" rel="noopener">
            Privacy Policy
          </a>
          .
        </p>
      </div>
      <PublicInfo />
    </div>
  );
}

function GateLoading({ who, onCancel }: { who: Provider; onCancel(): void }) {
  const grid = useFocusGrid({
    count: 1,
    active: true,
    priority: LAYER.MODAL,
    onConfirm: onCancel,
    onBack: onCancel,
  });
  return (
    <div
      class="hf-gate"
      role="dialog"
      aria-modal="true"
      aria-label="Signing in"
      data-testid="sign-in-gate-loading"
    >
      <div class="hf-dlg hf-dlg--sm">
        <div class="hf-dlg__spinner" aria-hidden="true" />
        <h2 class="t-display hf-dlg__title">SIGNING IN…</h2>
        <p class="hf-dlg__body">
          Waiting for {who}. Finish in the {who} window.
        </p>
        <p class="hf-dlg__fine">🔒 We only receive your name and a private ID.</p>
        <div class="hf-dlg__actions">
          <button type="button" class="hf-btn hf-btn--ghost interactive" {...grid.item(0)}>
            CANCEL
          </button>
        </div>
      </div>
    </div>
  );
}

function GateError(p: { msg: string; code: string; onRetry(): void; onOffline(): void }) {
  const grid = useFocusGrid({
    count: 2,
    cols: 2,
    horizontal: true,
    active: true,
    priority: LAYER.MODAL,
    onConfirm: (i) => (i === 0 ? p.onRetry() : p.onOffline()),
  });
  return (
    <div
      class="hf-gate"
      role="alertdialog"
      aria-modal="true"
      aria-label="Sign-in failed"
      data-testid="sign-in-gate-error"
    >
      <div class="hf-dlg hf-dlg--sm">
        <div class="hf-dlg__badge hf-dlg__badge--red" aria-hidden="true">
          !
        </div>
        <h2 class="t-display hf-dlg__title hf-dlg__title--white">FUMBLE!</h2>
        <p class="hf-dlg__body">{p.msg}</p>
        <code class="hf-dlg__code">Error: {p.code}</code>
        <div class="hf-dlg__actions">
          <button
            type="button"
            class="hf-btn hf-btn--gold interactive"
            data-testid="sign-in-retry"
            {...grid.item(0)}
          >
            ↻ TRY AGAIN
          </button>
          <button type="button" class="hf-btn hf-btn--outline interactive" {...grid.item(1)}>
            PLAY OFFLINE
          </button>
        </div>
      </div>
    </div>
  );
}
